import {
  AxesHelper,
  Bone,
  BufferAttribute,
  type Camera,
  Color,
  CylinderGeometry,
  Float32BufferAttribute,
  Group,
  Line,
  LineBasicMaterial,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Plane,
  Quaternion,
  Raycaster,
  Scene,
  SkinnedMesh,
  SphereGeometry,
  Vector2,
  Vector3,
  BufferGeometry
} from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { type TransformControls } from 'three/examples/jsm/controls/TransformControls.js'
import {
  type BoneDescriptor,
  type BoneDisplayShape,
  create_empty_descriptor,
  descriptor_from_armature,
  generate_skeleton_id,
  materialize_skeleton,
  type SkeletonDescriptor,
  type QuatXYZW,
  type Vec3
} from '../../../features/skeleton/SkeletonDescriptor'
import { BoneShapeGeometry } from './BoneShapeGeometry'
import { BoneTreeEditor, swap_side_suffix } from './BoneTreeEditor'
import { IkSolver, type SwingTwistLimits } from './IkSolver'

/**
 * StepBuildSkeleton
 *
 * Authoring host for the Build Skeleton mode. Owns:
 *  - the in-memory `BoneDescriptor[]` representing the skeleton being authored
 *  - the materialized Three.js armature visible in the scene
 *  - a small undo/redo stack of bone-array snapshots
 *  - selection state, click-to-add state, and transform-controls wiring
 *
 * It does NOT own the DOM panel. The UI panel reads state via `get_*` methods
 * and pushes user actions back via the `add_*` / `delete_*` / `rename_*` etc.
 * methods, then calls `request_render_callback` to repaint the bone list.
 *
 * This decoupling keeps the engine-level concerns (scene, raycasting, gizmo)
 * separate from the DOM-layer concerns (button enable states, list rendering).
 */
export type AddBoneMode = 'idle' | 'place_root' | 'place_child'
export type BuildEditMode = 'translate_head' | 'translate_tail' | 'rotate'

export interface StepBuildSkeletonOptions {
  scene: Scene
  camera: Camera
  transform_controls: TransformControls
  renderer_canvas: HTMLCanvasElement
}

interface RenderListener {
  (): void
}

export class StepBuildSkeleton extends EventTarget {
  private bones: BoneDescriptor[] = []
  private selected_bone_index: number | null = null
  private add_bone_mode: AddBoneMode = 'idle'
  private edit_mode: BuildEditMode = 'rotate'

  private mode: 'standalone' | 'inline' = 'standalone'
  private mesh: SkinnedMesh | undefined = undefined
  // Inline-mode raycast mesh handed in by `enter()`. Held separately from
  // `this.mesh` so a reference-GLB load/clear cycle restores the original
  // snap target instead of silently disabling snap-to-surface placement.
  private inline_mesh: SkinnedMesh | undefined = undefined
  private skeleton_id: string = generate_skeleton_id()
  private skeleton_name: string = 'Untitled Skeleton'
  private skeleton_created_at: string = new Date().toISOString()

  private readonly undo_stack: BoneDescriptor[][] = []
  private readonly redo_stack: BoneDescriptor[][] = []
  private readonly max_history = 50

  // Visuals owned by this step. Cleared in `dispose()`.
  private readonly visual_root: Group = new Group()
  private readonly bone_handles: Mesh[] = [] // index === bone index
  // Per-bone connector cylinder, parented to the bone's PARENT bone. Index by
  // bone index; entries for root bones (no parent) are undefined.
  private readonly bone_connectors: Array<Mesh | undefined> = []
  private selection_halo: Mesh | undefined = undefined
  private bone_display_thickness: number = 1.0
  private readonly base_head_radius: number = 0.025
  private readonly base_tail_radius: number = 0.018
  private readonly base_connector_radius: number = 0.012
  private readonly default_connector_color: number = 0x6e8aa8
  private readonly selected_connector_color: number = 0xff7e00
  private readonly default_head_color: number = 0x4cd964
  private readonly locked_head_color: number = 0x808080
  private readonly ik_target_head_color: number = 0xff3399

  // IK drag state. When the selected bone is an IK target and we're in
  // translate_head mode, transform-controls is attached to `ik_drag_handle`
  // (a world-space Object3D) instead of the bone itself. Each drag-frame the
  // solver bends the parent chain to follow the handle.
  private ik_drag_handle: Object3D | undefined = undefined
  private ik_change_listener: (() => void) | null = null
  /** Stored as a field so detach_ik_drag_handle can guarantee removal even
   *  when the user switches bones mid-drag (the listener would otherwise
   *  remain registered as a stale closure). */
  private ik_drag_end_listener: ((e: { value: boolean }) => void) | null = null
  private rotate_clamp_listener: (() => void) | null = null
  // Visual lines showing each IK target's pole direction. Indexed by IK
  // target bone index; entries for non-IK or pole-less bones are missing.
  private readonly pole_indicators: Map<number, Line> = new Map()

  private materialized: ReturnType<typeof materialize_skeleton> | undefined = undefined

  // Optional reference GLB the user loaded into the editor as a backdrop.
  // The visible scene is parented directly to `scene` (not `visual_root`) so it
  // survives `refresh_visuals()` which wipes the visuals root every commit.
  // Cleared in `dispose()` and replaced in `load_reference_glb_from_url()`.
  private reference_object: Object3D | undefined = undefined
  private reference_gltf_loader: GLTFLoader | undefined = undefined

  // Authoring options (toggles).
  private live_mirror_enabled: boolean = false
  private snap_to_vertex_enabled: boolean = false
  private axis_triads_visible: boolean = false
  private readonly bone_axis_helpers: AxesHelper[] = []
  // Snapshot of the reference mesh's original vertex colors so we can restore
  // them when the user disables the influence preview.
  private original_reference_vertex_colors: Map<BufferGeometry, Float32Array | null> = new Map()
  private influence_preview_enabled: boolean = false

  private readonly raycaster: Raycaster = new Raycaster()
  private readonly mouse: Vector2 = new Vector2()

  private readonly render_listeners: RenderListener[] = []
  private mouse_down_handler: ((e: MouseEvent) => void) | null = null
  private active: boolean = false

  constructor (private readonly options: StepBuildSkeletonOptions) {
    super()
    this.visual_root.name = 'BuildSkeleton Visuals'
  }

  // --- lifecycle ----------------------------------------------------------

  public enter (params: {
    mode: 'standalone' | 'inline'
    mesh?: SkinnedMesh
    initial?: SkeletonDescriptor
  }): void {
    if (this.active) this.dispose()

    this.mode = params.mode
    this.mesh = params.mesh
    this.inline_mesh = params.mesh

    if (params.initial !== undefined) {
      this.bones = params.initial.bones.map(clone_bone)
      this.skeleton_id = params.initial.id
      this.skeleton_name = params.initial.name
      this.skeleton_created_at = params.initial.created_at
    } else {
      const empty = create_empty_descriptor()
      this.bones = []
      this.skeleton_id = empty.id
      this.skeleton_name = empty.name
      this.skeleton_created_at = empty.created_at
    }

    this.options.scene.add(this.visual_root)
    this.mouse_down_handler = (e) => this.handle_canvas_mousedown(e)
    this.options.renderer_canvas.addEventListener('mousedown', this.mouse_down_handler)

    this.active = true
    this.refresh_visuals()
    this.notify_render_listeners()
  }

  public dispose (): void {
    if (!this.active) return
    this.active = false

    if (this.mouse_down_handler !== null) {
      this.options.renderer_canvas.removeEventListener('mousedown', this.mouse_down_handler)
      this.mouse_down_handler = null
    }

    this.detach_ik_drag_handle()
    this.detach_rotate_clamp_listener()
    this.options.transform_controls.detach()
    this.clear_visuals()
    this.clear_reference_object()
    this.options.scene.remove(this.visual_root)
    this.bones = []
    this.selected_bone_index = null
    this.add_bone_mode = 'idle'
    this.undo_stack.length = 0
    this.redo_stack.length = 0
  }

  public is_active (): boolean { return this.active }
  public get_mode (): 'standalone' | 'inline' { return this.mode }

  // --- read state for the UI panel ---------------------------------------

  public get_bones (): readonly BoneDescriptor[] { return this.bones }
  public get_selected_bone_index (): number | null { return this.selected_bone_index }
  public get_add_bone_mode (): AddBoneMode { return this.add_bone_mode }
  public get_edit_mode (): BuildEditMode { return this.edit_mode }
  public get_skeleton_name (): string { return this.skeleton_name }
  public get_skeleton_id (): string { return this.skeleton_id }
  public can_undo (): boolean { return this.undo_stack.length > 0 }
  public can_redo (): boolean { return this.redo_stack.length > 0 }

  public get_descriptor (): SkeletonDescriptor {
    return {
      format: 'meshinator-skeleton',
      version: 1,
      id: this.skeleton_id,
      name: this.skeleton_name,
      created_at: this.skeleton_created_at,
      updated_at: new Date().toISOString(),
      bones: this.bones.map(clone_bone)
    }
  }

  public set_skeleton_name (name: string): void {
    this.skeleton_name = name
  }

  public on_state_change (listener: RenderListener): () => void {
    this.render_listeners.push(listener)
    return () => {
      const i = this.render_listeners.indexOf(listener)
      if (i >= 0) this.render_listeners.splice(i, 1)
    }
  }

  // --- editing actions ----------------------------------------------------

  public select_bone (index: number | null): void {
    if (index !== null && (index < 0 || index >= this.bones.length)) return
    this.selected_bone_index = index
    this.attach_transform_controls_to_selection()
    this.notify_render_listeners()
  }

  public set_add_bone_mode (mode: AddBoneMode): void {
    if (mode === 'place_child' && this.selected_bone_index === null) {
      this.add_bone_mode = 'idle'
      return
    }
    this.add_bone_mode = mode
    this.notify_render_listeners()
  }

  public set_edit_mode (mode: BuildEditMode): void {
    this.edit_mode = mode
    this.attach_transform_controls_to_selection()
    this.notify_render_listeners()
  }

  public get_bone_display_thickness (): number { return this.bone_display_thickness }

  /**
   * Scale the on-screen size of head/tail bone handles. Pure visual — does
   * not change persisted descriptor data. Cheap; just adjusts existing handle
   * Mesh scales rather than rebuilding geometry.
   */
  public set_bone_display_thickness (value: number): void {
    if (!Number.isFinite(value) || value <= 0) return
    this.bone_display_thickness = value
    this.apply_bone_display_thickness()
  }

  private apply_bone_display_thickness (): void {
    for (const handle of this.bone_handles) {
      handle.scale.setScalar(this.bone_display_thickness)
    }
    if (this.selection_halo !== undefined) {
      // Halo is sized relative to the head handle plus a fixed multiplier.
      this.selection_halo.scale.setScalar(this.bone_display_thickness)
    }
    // Connectors: scale only X/Z (radial axes); Y carries the connector length
    // which is intrinsic to the bone offset and must NOT scale with thickness.
    for (const connector of this.bone_connectors) {
      if (connector === undefined) continue
      connector.scale.x = this.bone_display_thickness
      connector.scale.z = this.bone_display_thickness
    }
  }

  public has_reference_object (): boolean {
    return this.reference_object !== undefined
  }

  /**
   * Load a GLB as a visible reference in the editor viewport. If the GLB
   * contains a skinned armature, its bones are extracted into a
   * BoneDescriptor[] and replace the current skeleton (committed to undo so
   * the user can revert).
   *
   * The GLB's first SkinnedMesh becomes the raycast snap target for placing
   * new bones, replacing whatever inline mesh `enter()` was given.
   *
   * Returns counts so the caller can show a status message.
   */
  public async load_reference_glb_from_url (
    url: string,
    options: { replace_bones?: boolean } = {}
  ): Promise<{ bones_imported: number, has_mesh: boolean }> {
    if (!this.active) throw new Error('StepBuildSkeleton: load_reference_glb_from_url called before enter().')
    if (this.reference_gltf_loader === undefined) this.reference_gltf_loader = new GLTFLoader()
    const loader = this.reference_gltf_loader
    const gltf = await new Promise<{ scene: Object3D }>((resolve, reject) => {
      loader.load(url, resolve as (g: { scene: Object3D }) => void, undefined, reject)
    })

    this.clear_reference_object()
    const scene_root = gltf.scene
    scene_root.name = 'BuildSkeleton Reference GLB'
    // Resolve world matrices so the SkinnedMesh raycast is correct without
    // any further setup work in the visual layer.
    scene_root.updateMatrixWorld(true)
    this.options.scene.add(scene_root)
    this.reference_object = scene_root

    let first_skinned_mesh: SkinnedMesh | undefined
    scene_root.traverse((obj) => {
      if (first_skinned_mesh === undefined && obj instanceof SkinnedMesh) {
        first_skinned_mesh = obj
      }
    })
    if (first_skinned_mesh !== undefined) this.mesh = first_skinned_mesh

    let bones_imported = 0
    if (options.replace_bones !== false) {
      const has_bones = (() => {
        let found = false
        scene_root.traverse((obj) => { if (obj instanceof Bone) found = true })
        return found
      })()
      if (has_bones) {
        const imported = descriptor_from_armature(scene_root, this.skeleton_name)
        bones_imported = imported.bones.length
        if (bones_imported > 0) {
          // Drop selection before replacing — old indices won't survive.
          this.selected_bone_index = null
          this.commit(imported.bones.map(clone_bone))
        }
      }
    }

    return { bones_imported, has_mesh: first_skinned_mesh !== undefined }
  }

  public clear_reference_object (): void {
    // Restore vertex colors & material flags before disposing geometry, so we
    // don't leak vertex-color state if the user reloads a different reference.
    if (this.influence_preview_enabled) {
      this.clear_influence_preview()
    }
    this.original_reference_vertex_colors.clear()
    if (this.reference_object !== undefined) {
      this.options.scene.remove(this.reference_object)
      // Dispose geometries/materials we created via the GLTFLoader so the
      // GPU doesn't accumulate them when the user reloads multiple references.
      this.reference_object.traverse((obj) => {
        if (obj instanceof Mesh || obj instanceof SkinnedMesh) {
          obj.geometry.dispose()
          const mat = obj.material
          if (Array.isArray(mat)) mat.forEach(m => m.dispose())
          else mat.dispose()
        }
      })
      this.reference_object = undefined
    }
    // Restore the inline raycast mesh if any (undefined in standalone mode).
    this.mesh = this.inline_mesh
  }

  // --- authoring toggles --------------------------------------------------

  public is_live_mirror_enabled (): boolean { return this.live_mirror_enabled }
  public set_live_mirror_enabled (enabled: boolean): void {
    this.live_mirror_enabled = enabled
    this.notify_render_listeners()
  }

  public is_snap_to_vertex_enabled (): boolean { return this.snap_to_vertex_enabled }
  public set_snap_to_vertex_enabled (enabled: boolean): void {
    this.snap_to_vertex_enabled = enabled
    this.notify_render_listeners()
  }

  public are_axis_triads_visible (): boolean { return this.axis_triads_visible }
  public set_axis_triads_visible (visible: boolean): void {
    this.axis_triads_visible = visible
    for (const helper of this.bone_axis_helpers) {
      helper.visible = visible
    }
    this.notify_render_listeners()
  }

  public is_influence_preview_enabled (): boolean { return this.influence_preview_enabled }
  public set_influence_preview_enabled (enabled: boolean): void {
    this.influence_preview_enabled = enabled
    if (enabled) {
      this.apply_influence_preview()
    } else {
      this.clear_influence_preview()
    }
    this.notify_render_listeners()
  }

  // --- bone editing actions added in this round --------------------------

  /**
   * Add a new child of the currently selected bone whose head sits at the
   * parent's tail position — building a connected chain. The new bone's tail
   * extends along the parent's head→tail direction at the same length, so
   * repeated clicks produce an evenly spaced chain (e.g., spine joints).
   */
  public add_connected_child_bone (): void {
    if (this.selected_bone_index === null) return
    const parent = this.bones[this.selected_bone_index]
    // Parent's local frame has head at origin; tail is at parent.tail.
    const head: Vec3 = [parent.tail[0], parent.tail[1], parent.tail[2]]
    const tail_vec = new Vector3(parent.tail[0], parent.tail[1], parent.tail[2])
    const length = tail_vec.length()
    const direction = length > 1e-5 ? tail_vec.clone().normalize() : new Vector3(0, 1, 0)
    const segment = Math.max(length, 0.1)
    const tail: Vec3 = [
      head[0] + direction.x * segment,
      head[1] + direction.y * segment,
      head[2] + direction.z * segment
    ]
    const name = BoneTreeEditor.suggest_unique_name(this.bones, 'bone')
    const next = BoneTreeEditor.add_child_bone(this.bones, this.selected_bone_index, name, head, tail)
    const new_index = next.length - 1
    this.commit(next)
    this.select_bone(new_index)
  }

  /**
   * Reparent the selected bone to a new parent (or to root if null). Throws
   * via BoneTreeEditor.reparent if the result would create a cycle; caller
   * should catch and surface the message.
   */
  public reparent_selected_bone (new_parent_index: number | null): void {
    if (this.selected_bone_index === null) return
    if (this.bones[this.selected_bone_index].locked === true) return
    const next = BoneTreeEditor.reparent(this.bones, this.selected_bone_index, new_parent_index)
    if (next === this.bones) return
    this.commit(next)
  }

  /**
   * Apply a name mapping (find/replace) to every bone. Renames in dependency
   * order so collisions inside the mapping resolve cleanly. Returns the
   * number of bones whose names actually changed.
   */
  public apply_name_mapping (find: string, replace: string, options: { case_insensitive?: boolean } = {}): number {
    if (find.length === 0) return 0
    const flags = options.case_insensitive === true ? 'gi' : 'g'
    const regex = new RegExp(escape_regex(find), flags)
    let next = this.bones.slice()
    let renamed = 0
    // First compute proposed renames; skip bones whose new name would collide
    // with another existing name we are not also renaming.
    const proposed = new Map<number, string>()
    next.forEach((bone, i) => {
      const after = bone.name.replace(regex, replace)
      if (after !== bone.name) proposed.set(i, after)
    })
    // Apply two-phase to avoid intermediate-collision failures: stamp every
    // proposed name to a guaranteed-unique placeholder, then to the target.
    const placeholders = new Map<number, string>()
    proposed.forEach((target, i) => {
      placeholders.set(i, `__rename_pending_${i}_${Date.now()}`)
      next = BoneTreeEditor.rename_bone(next, i, placeholders.get(i) as string)
    })
    proposed.forEach((target, i) => {
      // If the final target name collides with a non-renamed bone, skip it.
      const collision = next.some((b, j) => j !== i && b.name === target)
      if (collision) {
        // Restore original by replacing placeholder with what it was. Find the
        // pre-rename name using the source bones snapshot.
        next = BoneTreeEditor.rename_bone(next, i, this.bones[i].name)
      } else {
        next = BoneTreeEditor.rename_bone(next, i, target)
        renamed++
      }
    })
    if (renamed > 0) this.commit(next)
    return renamed
  }

  /**
   * Apply an arbitrary transform to every bone name in a single commit. Used
   * by presets like "lowercase everything" where a regex find/replace would
   * either need 26 separate calls (one per letter, polluting undo) or a
   * separate code path. Bones whose new name would collide with another bone
   * are left at their original name. Returns the number renamed.
   */
  public transform_bone_names (transform: (name: string) => string): number {
    let next = this.bones.slice()
    const proposed = new Map<number, string>()
    next.forEach((b, i) => {
      const after = transform(b.name)
      if (after !== b.name) proposed.set(i, after)
    })
    if (proposed.size === 0) return 0
    // Two-phase rename via placeholders, identical pattern to apply_name_mapping.
    proposed.forEach((_, i) => {
      next = BoneTreeEditor.rename_bone(next, i, `__rename_pending_${i}_${Date.now()}`)
    })
    let renamed = 0
    proposed.forEach((target, i) => {
      const collision = next.some((b, j) => j !== i && b.name === target)
      if (collision) {
        next = BoneTreeEditor.rename_bone(next, i, this.bones[i].name)
      } else {
        next = BoneTreeEditor.rename_bone(next, i, target)
        renamed++
      }
    })
    if (renamed > 0) this.commit(next)
    return renamed
  }

  /**
   * Delete bones whose tail offset (bone length in its own local space) is
   * smaller than `min_length`. Children reparent to the deleted bone's parent
   * via `delete_bone_reparent_children`, so chains stay connected. Returns
   * the number of bones removed. Locked bones are skipped.
   */
  public prune_small_bones (min_length: number): number {
    if (!Number.isFinite(min_length) || min_length <= 0) return 0
    let next = this.bones.slice()
    let removed = 0
    // Iterate from the end so indices ahead stay stable as we splice.
    let i = next.length - 1
    while (i >= 0) {
      const b = next[i]
      if (b.locked !== true) {
        const len = Math.hypot(b.tail[0], b.tail[1], b.tail[2])
        if (len < min_length) {
          next = BoneTreeEditor.delete_bone_reparent_children(next, i)
          removed++
        }
      }
      i--
    }
    if (removed > 0) {
      // Selection indices likely invalidated.
      this.selected_bone_index = null
      this.commit(next)
    }
    return removed
  }

  /**
   * Detect mirrored bone pairs by world-X position symmetry and add `_l`/`_r`
   * suffixes to their names. Only renames bones that don't already have a
   * recognized side suffix. A pair is `(a, b)` such that `a.head ≈ -b.head`
   * on the X axis (within tolerance) and `a.head.y ≈ b.head.y`,
   * `a.head.z ≈ b.head.z`. Bones close to the X=0 centerline are left alone.
   * Returns the number of bones renamed.
   */
  public auto_symmetrize_names (tolerance: number = 0.01): number {
    // We need world positions, not local — materialize a temporary scene.
    if (this.bones.length === 0) return 0
    const m = materialize_skeleton(this.get_descriptor())
    const world_positions = m.bones.map((b) => b.getWorldPosition(new Vector3()))

    let renamed_count = 0
    let next = this.bones.slice()
    const renamed_indices = new Set<number>()
    for (let i = 0; i < next.length; i++) {
      if (renamed_indices.has(i)) continue
      if (swap_side_suffix(next[i].name) !== null) continue // already sided
      const pi = world_positions[i]
      if (Math.abs(pi.x) < tolerance) continue // centerline
      for (let j = i + 1; j < next.length; j++) {
        if (renamed_indices.has(j)) continue
        if (swap_side_suffix(next[j].name) !== null) continue
        const pj = world_positions[j]
        if (
          Math.abs(pi.x + pj.x) < tolerance &&
          Math.abs(pi.y - pj.y) < tolerance &&
          Math.abs(pi.z - pj.z) < tolerance
        ) {
          // Conventional: positive X → "_l", negative X → "_r".
          const left_idx = pi.x > 0 ? i : j
          const right_idx = pi.x > 0 ? j : i
          const base_left = next[left_idx].name
          const base_right = next[right_idx].name
          const proposed_left = `${base_left}_l`
          const proposed_right = `${base_right}_r`
          // Skip if the proposed names would collide.
          if (next.some((b, k) => k !== left_idx && b.name === proposed_left)) continue
          if (next.some((b, k) => k !== right_idx && b.name === proposed_right)) continue
          next = BoneTreeEditor.rename_bone(next, left_idx, proposed_left)
          next = BoneTreeEditor.rename_bone(next, right_idx, proposed_right)
          renamed_indices.add(left_idx)
          renamed_indices.add(right_idx)
          renamed_count += 2
          break
        }
      }
    }
    if (renamed_count > 0) this.commit(next)
    return renamed_count
  }

  public rename_selected_bone (new_name: string): void {
    if (this.selected_bone_index === null) return
    if (this.bones[this.selected_bone_index].locked === true) return
    const next = BoneTreeEditor.rename_bone(this.bones, this.selected_bone_index, new_name)
    if (next === this.bones) return
    this.commit(next)
  }

  /**
   * Delete the selected bone and reparent its direct children to its parent.
   * Default destructive action — leaves the rest of the chain intact.
   */
  public delete_selected_bone (): void {
    if (this.selected_bone_index === null) return
    if (this.bones[this.selected_bone_index].locked === true) return
    const next = BoneTreeEditor.delete_bone_reparent_children(this.bones, this.selected_bone_index)
    this.selected_bone_index = null
    this.commit(next)
  }

  /**
   * Delete the selected bone AND every descendant beneath it. More destructive
   * than `delete_selected_bone`; intended for removing a whole sub-chain like
   * an arm or a finger group.
   */
  public delete_selected_bone_with_descendants (): void {
    if (this.selected_bone_index === null) return
    if (this.bones[this.selected_bone_index].locked === true) return
    const next = BoneTreeEditor.delete_bone_and_descendants(this.bones, this.selected_bone_index)
    this.selected_bone_index = null
    this.commit(next)
  }

  /**
   * Mirror the selected bone and all its descendants across the X axis,
   * creating `_l`/`_r` (or `.l`/`.r`) counterparts. Returns true on success,
   * or an error message on failure (no bone selected, no side suffix, name
   * collision).
   */
  public mirror_selected_bone (): { ok: true } | { ok: false, message: string } {
    if (this.selected_bone_index === null) {
      return { ok: false, message: 'Select a bone first.' }
    }
    try {
      const next = BoneTreeEditor.mirror_bone_subtree(this.bones, this.selected_bone_index)
      this.commit(next)
      return { ok: true }
    } catch (err) {
      return { ok: false, message: (err as Error).message }
    }
  }

  /**
   * Set the IK chain length for the selected bone. 0 = not an IK target.
   * Greater than zero = IK target whose parent N bones bend to follow it
   * during drag.
   */
  public set_selected_bone_ik_chain_length (length: number): void {
    if (this.selected_bone_index === null) return
    const next = BoneTreeEditor.set_ik_chain_length(this.bones, this.selected_bone_index, length)
    if (next === this.bones) return
    this.commit(next)
  }

  public is_bone_ik_target (index: number): boolean {
    if (index < 0 || index >= this.bones.length) return false
    return (this.bones[index].ik_chain_length ?? 0) > 0
  }

  public set_selected_bone_pole_vector (pole: Vec3 | null): void {
    if (this.selected_bone_index === null) return
    const next = BoneTreeEditor.set_pole_vector(this.bones, this.selected_bone_index, pole)
    if (next === this.bones) return
    this.commit(next)
  }

  public set_selected_bone_rotation_limits (
    limits: NonNullable<BoneDescriptor['rotation_limits']> | null
  ): void {
    if (this.selected_bone_index === null) return
    const next = BoneTreeEditor.set_rotation_limits(this.bones, this.selected_bone_index, limits)
    if (next === this.bones) return
    this.commit(next)
  }

  public set_selected_bone_display_shape (shape: BoneDisplayShape | null): void {
    if (this.selected_bone_index === null) return
    const next = BoneTreeEditor.set_display_shape(this.bones, this.selected_bone_index, shape)
    if (next === this.bones) return
    this.commit(next)
  }

  public toggle_bone_lock (index: number): void {
    if (index < 0 || index >= this.bones.length) return
    const new_locked = this.bones[index].locked !== true
    const next = BoneTreeEditor.set_locked(this.bones, index, new_locked)
    if (next === this.bones) return
    this.commit(next)
    // If we just locked the currently selected bone, drop the gizmo so the
    // user can't keep dragging it.
    if (new_locked && index === this.selected_bone_index) {
      this.attach_transform_controls_to_selection()
    }
  }

  public is_bone_locked (index: number): boolean {
    if (index < 0 || index >= this.bones.length) return false
    return this.bones[index].locked === true
  }

  public undo (): void {
    if (this.undo_stack.length === 0) return
    const previous = this.undo_stack.pop() as BoneDescriptor[]
    this.redo_stack.push(this.bones)
    this.bones = previous
    if (this.selected_bone_index !== null && this.selected_bone_index >= this.bones.length) {
      this.selected_bone_index = null
    }
    this.refresh_visuals()
    this.notify_render_listeners()
  }

  public redo (): void {
    if (this.redo_stack.length === 0) return
    const next = this.redo_stack.pop() as BoneDescriptor[]
    this.undo_stack.push(this.bones)
    this.bones = next
    this.refresh_visuals()
    this.notify_render_listeners()
  }

  // --- mouse interaction --------------------------------------------------

  private handle_canvas_mousedown (e: MouseEvent): void {
    if (!this.active) return
    if (e.button !== 0) return // left click only
    // If transform-controls is dragging the gizmo, ignore.
    if (this.options.transform_controls.dragging) return

    const rect = this.options.renderer_canvas.getBoundingClientRect()
    this.mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1
    this.mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1
    this.raycaster.setFromCamera(this.mouse, this.options.camera)

    if (this.add_bone_mode === 'place_root' || this.add_bone_mode === 'place_child') {
      const world_point = this.raycast_placement_target()
      if (world_point !== null) {
        this.insert_bone_at(world_point)
      }
      return
    }

    // Otherwise: attempt to select a bone by clicking its handle.
    const hit = this.raycaster.intersectObjects(this.bone_handles, false)
    if (hit.length > 0) {
      const idx = this.bone_handles.indexOf(hit[0].object as Mesh)
      if (idx >= 0) this.select_bone(idx)
    }
  }

  private raycast_placement_target (): Vector3 | null {
    // Inline mode: prefer mesh surface for snapping; fall back to floor plane.
    if (this.mesh !== undefined) {
      const hit = this.raycaster.intersectObject(this.mesh, true)
      if (hit.length > 0) {
        if (this.snap_to_vertex_enabled) {
          const snapped = this.snap_hit_to_nearest_vertex(hit[0])
          if (snapped !== null) return snapped
        }
        return hit[0].point.clone()
      }
    }
    // Floor plane at y=0.
    const floor = new Plane(new Vector3(0, 1, 0), 0)
    const target = new Vector3()
    if (this.raycaster.ray.intersectPlane(floor, target) !== null) return target
    return null
  }

  /**
   * Snap a raycast hit to the nearest vertex of the hit triangle. Returns
   * the world-space position of that vertex, or null if the geometry doesn't
   * expose enough info to compute (no face index, non-indexed without a/b/c).
   */
  private snap_hit_to_nearest_vertex (hit: { object: Object3D, point: Vector3, face?: { a: number, b: number, c: number } | null }): Vector3 | null {
    const obj = hit.object as Mesh
    const geom = obj.geometry as BufferGeometry | undefined
    const face = hit.face
    if (geom === undefined || face === null || face === undefined) return null
    const pos_attr = geom.attributes.position
    if (pos_attr === undefined) return null
    const va = new Vector3().fromBufferAttribute(pos_attr, face.a).applyMatrix4(obj.matrixWorld)
    const vb = new Vector3().fromBufferAttribute(pos_attr, face.b).applyMatrix4(obj.matrixWorld)
    const vc = new Vector3().fromBufferAttribute(pos_attr, face.c).applyMatrix4(obj.matrixWorld)
    const da = va.distanceToSquared(hit.point)
    const db = vb.distanceToSquared(hit.point)
    const dc = vc.distanceToSquared(hit.point)
    if (da <= db && da <= dc) return va
    if (db <= dc) return vb
    return vc
  }

  private insert_bone_at (world_point: Vector3): void {
    const head_local: Vec3 = [world_point.x, world_point.y, world_point.z]
    const tail_local: Vec3 = [world_point.x, world_point.y + 0.1, world_point.z]
    let next: BoneDescriptor[]
    let new_index: number

    if (this.add_bone_mode === 'place_root' || this.selected_bone_index === null) {
      const name = BoneTreeEditor.suggest_unique_name(this.bones, 'bone')
      next = BoneTreeEditor.add_root_bone(this.bones, name, head_local, tail_local)
      new_index = next.length - 1
    } else {
      const parent_index = this.selected_bone_index
      // Convert world position to parent's local space.
      const parent_world = this.materialized?.bones[parent_index]
      const local_head = parent_world !== undefined
        ? parent_world.worldToLocal(world_point.clone())
        : world_point.clone()
      const local_tail = local_head.clone().add(new Vector3(0, 0.1, 0))
      const name = BoneTreeEditor.suggest_unique_name(this.bones, 'bone')
      next = BoneTreeEditor.add_child_bone(
        this.bones,
        parent_index,
        name,
        [local_head.x, local_head.y, local_head.z],
        [local_tail.x, local_tail.y, local_tail.z]
      )
      new_index = next.length - 1
    }

    this.add_bone_mode = 'idle'
    this.commit(next)
    this.select_bone(new_index)
  }

  /**
   * Commit the current selected bone's transform from the materialized scene
   * back into the BoneDescriptor (after a transform-controls drag finishes).
   * Called by the engine's transform-controls 'mouseUp' event handler when
   * Build Skeleton is the active step.
   */
  public commit_transform_from_scene (): void {
    if (this.selected_bone_index === null) return
    if (this.materialized === undefined) return
    if (this.bones[this.selected_bone_index].locked === true) return
    const bone = this.materialized.bones[this.selected_bone_index]
    let next: BoneDescriptor[]
    if (this.edit_mode === 'rotate') {
      // If the bone has rotation limits, clamp the gizmo's resulting
      // quaternion in-place before persisting and reflect the clamp back to
      // the scene bone so the visual matches.
      const limits = this.bones[this.selected_bone_index].rotation_limits
      if (limits !== undefined) {
        IkSolver.clamp_local_quaternion(bone.quaternion, limits)
        bone.updateMatrixWorld(true)
      }
      next = BoneTreeEditor.set_rotation(this.bones, this.selected_bone_index,
        [bone.quaternion.x, bone.quaternion.y, bone.quaternion.z, bone.quaternion.w])
    } else if (this.edit_mode === 'translate_head') {
      next = BoneTreeEditor.set_head(this.bones, this.selected_bone_index,
        [bone.position.x, bone.position.y, bone.position.z])
    } else {
      // translate_tail: tail handle drag — the gizmo is attached to a tail proxy
      // whose local position relative to the bone is what we capture.
      const tail_handle = this.bone_handles[this.selected_bone_index + this.bones.length] // tail handle stored after head handles
      if (tail_handle === undefined) return
      const local = bone.worldToLocal(tail_handle.getWorldPosition(new Vector3()))
      next = BoneTreeEditor.set_tail(this.bones, this.selected_bone_index, [local.x, local.y, local.z])
    }
    this.commit(next, { skip_visual_refresh: true })
    if (this.live_mirror_enabled && this.selected_bone_index !== null) {
      this.mirror_to_counterpart(this.selected_bone_index)
    }
  }

  /**
   * If `source_index` has a recognizable side suffix and a counterpart bone
   * exists by name swap, copy the source's head/tail/rotation to the
   * counterpart with X-mirroring. Used by Live Mirror mode after a transform
   * commit so paired limbs stay symmetric.
   */
  private mirror_to_counterpart (source_index: number): void {
    if (source_index < 0 || source_index >= this.bones.length) return
    const source = this.bones[source_index]
    const counterpart_name = swap_side_suffix(source.name)
    if (counterpart_name === null) return
    const counterpart_index = this.bones.findIndex(b => b.name === counterpart_name)
    if (counterpart_index < 0) return
    if (this.bones[counterpart_index].locked === true) return
    const next = this.bones.slice()
    const c: BoneDescriptor = { ...next[counterpart_index] }
    c.head = [-source.head[0], source.head[1], source.head[2]]
    c.tail = [-source.tail[0], source.tail[1], source.tail[2]]
    const q = source.rotation_quaternion
    c.rotation_quaternion = [-q[0], q[1], q[2], -q[3]]
    next[counterpart_index] = c
    this.commit(next)
  }

  // --- internal helpers ---------------------------------------------------

  private commit (next: BoneDescriptor[], options: { skip_visual_refresh?: boolean } = {}): void {
    if (next === this.bones) return
    this.undo_stack.push(this.bones)
    if (this.undo_stack.length > this.max_history) this.undo_stack.shift()
    this.redo_stack.length = 0
    this.bones = next
    if (options.skip_visual_refresh !== true) this.refresh_visuals()
    this.notify_render_listeners()
  }

  private refresh_visuals (): void {
    this.clear_visuals()
    if (this.bones.length === 0) {
      this.materialized = undefined
      return
    }

    this.materialized = materialize_skeleton(this.get_descriptor())
    this.visual_root.add(this.materialized.armature)

    // Cylinder connectors between each non-root bone and its parent. Parented
    // to the parent bone so they follow rotation/translation automatically.
    // Cylinder default geometry is along Y axis with height 1, so we orient
    // it to point from parent origin (its local 0,0,0) toward the child's
    // local position, then scale Y by the offset length.
    const connector_geo = new CylinderGeometry(this.base_connector_radius, this.base_connector_radius, 1, 8, 1)
    const up = new Vector3(0, 1, 0)
    for (let i = 0; i < this.bones.length; i++) {
      const parent_index = this.bones[i].parent_index
      if (parent_index === null) {
        this.bone_connectors.push(undefined)
        continue
      }
      const parent_bone = this.materialized.bones[parent_index]
      const head_local = new Vector3(this.bones[i].head[0], this.bones[i].head[1], this.bones[i].head[2])
      const length = head_local.length()
      // Per-connector material so selection-highlight color toggles don't
      // bleed across bones.
      const connector_mat = new MeshBasicMaterial({ color: this.default_connector_color, depthTest: false })
      const connector = new Mesh(connector_geo, connector_mat)
      connector.renderOrder = 998
      connector.name = `bone_connector_${i}`
      if (length > 1e-5) {
        const direction = head_local.clone().normalize()
        const quat = new Quaternion().setFromUnitVectors(up, direction)
        connector.quaternion.copy(quat)
        connector.scale.set(1, length, 1)
        connector.position.copy(head_local).multiplyScalar(0.5)
      } else {
        connector.scale.set(1, 0, 1)
      }
      parent_bone.add(connector)
      this.bone_connectors.push(connector)
    }

    // Build head + tail handles for each bone. Head materials are per-bone so
    // they can be tinted gray when locked; tail handles share one material.
    // Head GEOMETRY is also per-bone — looked up from the shape cache by the
    // bone's `display_shape` field. Cache returns a shared geometry per
    // shape name; never disposed by clear_visuals (kept for the lifetime of
    // the page since shape geometries are tiny and re-created on every
    // refresh would be wasteful).
    const tail_geo = new SphereGeometry(this.base_tail_radius, 6, 5)
    const tail_mat = new MeshBasicMaterial({ color: 0xffcc00, depthTest: false })

    for (let i = 0; i < this.bones.length; i++) {
      const shape: BoneDisplayShape = this.bones[i].display_shape ?? 'sphere'
      const head_geo = BoneShapeGeometry.get(shape, this.base_head_radius)
      const head_mat = new MeshBasicMaterial({ color: this.default_head_color, depthTest: false })
      const head_handle = new Mesh(head_geo, head_mat)
      head_handle.renderOrder = 999
      head_handle.name = `bone_head_${i}`
      this.materialized.bones[i].add(head_handle)
      head_handle.position.set(0, 0, 0)
      this.bone_handles.push(head_handle)
    }
    for (let i = 0; i < this.bones.length; i++) {
      const tail_local = this.bones[i].tail
      const tail_handle = new Mesh(tail_geo, tail_mat)
      tail_handle.renderOrder = 999
      tail_handle.name = `bone_tail_${i}`
      this.materialized.bones[i].add(tail_handle)
      tail_handle.position.set(tail_local[0], tail_local[1], tail_local[2])
      this.bone_handles.push(tail_handle)
    }

    // Selection halo — a single bright ring that follows whichever bone is
    // currently selected. Re-parented in attach_transform_controls_to_selection
    // rather than being one halo per bone, so memory stays constant.
    const halo_geo = new SphereGeometry(this.base_head_radius * 1.9, 12, 8)
    const halo_mat = new MeshBasicMaterial({
      color: 0xff7e00,
      wireframe: true,
      depthTest: false,
      transparent: true,
      opacity: 0.9
    })
    this.selection_halo = new Mesh(halo_geo, halo_mat)
    this.selection_halo.renderOrder = 1000
    this.selection_halo.visible = false

    // Bone-axis triads (one AxesHelper per bone, parented to the bone). Visible
    // only when `axis_triads_visible` is on. Helpers are tracked in a separate
    // array so toggling visibility is O(N) without rebuilding geometry.
    this.bone_axis_helpers.length = 0
    for (let i = 0; i < this.bones.length; i++) {
      const helper = new AxesHelper(this.base_head_radius * 4)
      helper.name = `bone_axes_${i}`
      helper.visible = this.axis_triads_visible
      // AxesHelper depthTest is on by default — we want it visible through the
      // mesh just like the bone handles. Materials may be a single LineBasicMaterial
      // or an array; iterate either way.
      const helper_mat = (helper as Line).material
      const mats = Array.isArray(helper_mat) ? helper_mat : [helper_mat]
      for (const mat of mats) {
        if (mat instanceof LineBasicMaterial) {
          mat.depthTest = false
          mat.transparent = true
        }
      }
      helper.renderOrder = 1001
      this.materialized.bones[i].add(helper)
      this.bone_axis_helpers.push(helper)
    }

    this.apply_bone_display_thickness()
    this.apply_lock_visuals()
    this.rebuild_pole_indicators()
    this.attach_transform_controls_to_selection()
    if (this.influence_preview_enabled) this.apply_influence_preview()
  }

  // --- influence preview --------------------------------------------------

  /**
   * Color the reference mesh's vertices by the index of the nearest bone (in
   * world space). Cheap O(V * B) approximation — does NOT compute proper
   * skinning weights; intended only as a visual sanity check while authoring
   * the skeleton ("does each region of the mesh have a bone near it?").
   */
  private apply_influence_preview (): void {
    if (this.materialized === undefined || this.bones.length === 0) return
    const meshes = this.collect_reference_meshes()
    if (meshes.length === 0) return

    const bone_world_positions: Vector3[] = this.materialized.bones.map(b => b.getWorldPosition(new Vector3()))
    const palette = this.bones.map((_, i) => generate_palette_color(i))

    for (const mesh of meshes) {
      const geom = mesh.geometry
      const pos_attr = geom.attributes.position
      if (pos_attr === undefined) continue
      // Snapshot original colors once so we can restore them on disable. Stored
      // per-geometry; null means "no original color attribute existed".
      if (!this.original_reference_vertex_colors.has(geom)) {
        const existing = geom.attributes.color
        if (existing !== undefined) {
          this.original_reference_vertex_colors.set(geom, new Float32Array(existing.array as Float32Array))
        } else {
          this.original_reference_vertex_colors.set(geom, null)
        }
      }
      const colors = new Float32Array(pos_attr.count * 3)
      const v = new Vector3()
      for (let vi = 0; vi < pos_attr.count; vi++) {
        v.fromBufferAttribute(pos_attr, vi).applyMatrix4(mesh.matrixWorld)
        let best_index = 0
        let best_dsq = Infinity
        for (let bi = 0; bi < bone_world_positions.length; bi++) {
          const dsq = v.distanceToSquared(bone_world_positions[bi])
          if (dsq < best_dsq) { best_dsq = dsq; best_index = bi }
        }
        const color = palette[best_index]
        colors[vi * 3] = color.r
        colors[vi * 3 + 1] = color.g
        colors[vi * 3 + 2] = color.b
      }
      geom.setAttribute('color', new BufferAttribute(colors, 3))
      const material = mesh.material
      const mats = Array.isArray(material) ? material : [material]
      for (const mat of mats) {
        if (mat instanceof MeshBasicMaterial) {
          mat.vertexColors = true
          mat.needsUpdate = true
        } else if ('vertexColors' in mat) {
          // PBR / Phong materials also support vertexColors.
          ;(mat as { vertexColors: boolean, needsUpdate: boolean }).vertexColors = true
          ;(mat as { needsUpdate: boolean }).needsUpdate = true
        }
      }
    }
  }

  private clear_influence_preview (): void {
    const meshes = this.collect_reference_meshes()
    for (const mesh of meshes) {
      const geom = mesh.geometry
      const original = this.original_reference_vertex_colors.get(geom)
      if (original === undefined) continue
      if (original === null) {
        geom.deleteAttribute('color')
      } else {
        geom.setAttribute('color', new BufferAttribute(original, 3))
      }
      const material = mesh.material
      const mats = Array.isArray(material) ? material : [material]
      for (const mat of mats) {
        if ('vertexColors' in mat) {
          ;(mat as { vertexColors: boolean, needsUpdate: boolean }).vertexColors = original !== null
          ;(mat as { needsUpdate: boolean }).needsUpdate = true
        }
      }
    }
    this.original_reference_vertex_colors.clear()
  }

  private collect_reference_meshes (): Mesh[] {
    const out: Mesh[] = []
    if (this.reference_object !== undefined) {
      this.reference_object.traverse((o) => {
        if (o instanceof Mesh || o instanceof SkinnedMesh) out.push(o as Mesh)
      })
    }
    return out
  }

  private rebuild_pole_indicators (): void {
    for (const line of this.pole_indicators.values()) {
      if (line.parent !== null) line.parent.remove(line)
      line.geometry.dispose()
      const m = line.material
      if (Array.isArray(m)) m.forEach(mm => mm.dispose())
      else m.dispose()
    }
    this.pole_indicators.clear()
    if (this.materialized === undefined) return

    for (let i = 0; i < this.bones.length; i++) {
      const pole = this.bones[i].pole_vector
      const chain_length = this.bones[i].ik_chain_length ?? 0
      if (pole === undefined || chain_length === 0) continue

      // Find the chain root: walk up `chain_length` parents.
      let cursor: Bone | null = this.materialized.bones[i].parent instanceof Object &&
        (this.materialized.bones[i].parent as Bone).isBone === true
        ? this.materialized.bones[i].parent as Bone : null
      let walked = 0
      let chain_root: Bone | null = cursor
      while (cursor !== null && walked < chain_length) {
        chain_root = cursor
        walked++
        cursor = cursor.parent instanceof Object && (cursor.parent as Bone).isBone === true ? cursor.parent as Bone : null
      }
      if (chain_root === null) continue

      // Build a line in world space from chain root + pole direction.
      const geometry = new BufferGeometry()
      geometry.setAttribute('position', new Float32BufferAttribute([0, 0, 0, pole[0], pole[1], pole[2]], 3))
      const material = new LineBasicMaterial({ color: 0xff3399, depthTest: false, transparent: true, opacity: 0.85 })
      const line = new Line(geometry, material)
      line.renderOrder = 997
      line.name = `pole_indicator_${i}`
      // Parent to the chain root bone so the line follows the bone's world
      // transform but is rendered from the bone's origin in world space.
      // Using a top-level scene attachment with manual world position would
      // also work; parenting is simpler and auto-updates.
      chain_root.add(line)
      this.pole_indicators.set(i, line)
    }
  }

  private apply_lock_visuals (): void {
    for (let i = 0; i < this.bones.length; i++) {
      const head_handle = this.bone_handles[i]
      if (head_handle === undefined) continue
      const mat = head_handle.material
      if (!(mat instanceof MeshBasicMaterial)) continue
      // Locked takes priority over IK; if neither, default green.
      let color = this.default_head_color
      if (this.bones[i].locked === true) {
        color = this.locked_head_color
      } else if ((this.bones[i].ik_chain_length ?? 0) > 0) {
        color = this.ik_target_head_color
      }
      mat.color.setHex(color)
    }
  }

  private attach_transform_controls_to_selection (): void {
    const tc = this.options.transform_controls
    this.detach_ik_drag_handle()
    this.detach_rotate_clamp_listener()
    this.update_selection_halo()
    if (this.selected_bone_index === null || this.materialized === undefined) {
      tc.detach()
      return
    }
    if (this.bones[this.selected_bone_index].locked === true) {
      tc.detach()
      return
    }
    const bone = this.materialized.bones[this.selected_bone_index]
    const is_ik = this.is_bone_ik_target(this.selected_bone_index)
    if (this.edit_mode === 'translate_tail') {
      const tail_handle = this.bone_handles[this.bones.length + this.selected_bone_index]
      if (tail_handle !== undefined) {
        tc.attach(tail_handle)
        tc.setMode('translate')
      }
    } else if (is_ik && this.edit_mode === 'translate_head') {
      // IK mode: attach to a world-space drag handle initialized at the
      // bone's current head position. As the user drags it, the solver
      // bends the parent chain so the bone reaches the handle.
      this.attach_ik_drag_handle(bone)
      tc.setMode('translate')
    } else {
      tc.attach(bone)
      tc.setMode(this.edit_mode === 'rotate' ? 'rotate' : 'translate')
      // Live-clamp the bone's quaternion during rotate-gizmo drag if the
      // bone has swing-twist limits. Without this, the gizmo lets the user
      // visually rotate past the limit and the clamp only bites at commit.
      if (this.edit_mode === 'rotate') {
        this.attach_rotate_clamp_listener()
      }
    }
    tc.enabled = true
  }

  private attach_rotate_clamp_listener (): void {
    if (this.selected_bone_index === null || this.materialized === undefined) return
    const limits = this.bones[this.selected_bone_index].rotation_limits
    if (limits === undefined) return
    const bone = this.materialized.bones[this.selected_bone_index]
    this.rotate_clamp_listener = () => {
      IkSolver.clamp_local_quaternion(bone.quaternion, limits)
      bone.updateMatrixWorld(true)
    }
    this.options.transform_controls.addEventListener('change', this.rotate_clamp_listener)
  }

  private detach_rotate_clamp_listener (): void {
    if (this.rotate_clamp_listener !== null) {
      this.options.transform_controls.removeEventListener('change', this.rotate_clamp_listener)
      this.rotate_clamp_listener = null
    }
  }

  private attach_ik_drag_handle (ik_target_bone: Bone): void {
    if (this.selected_bone_index === null) return
    const handle = new Object3D()
    handle.name = 'ik_drag_handle'
    ik_target_bone.updateMatrixWorld(true)
    ik_target_bone.getWorldPosition(handle.position)
    this.options.scene.add(handle)
    this.ik_drag_handle = handle
    this.options.transform_controls.attach(handle)

    const ik_index = this.selected_bone_index
    const chain_length = this.bones[ik_index].ik_chain_length ?? 0
    const pole_descriptor = this.bones[ik_index].pole_vector
    const pole_vector_world = pole_descriptor !== undefined
      ? new Vector3(pole_descriptor[0], pole_descriptor[1], pole_descriptor[2])
      : undefined
    const limits_map = this.build_chain_limits_map(ik_index, chain_length)
    this.ik_change_listener = () => {
      if (this.materialized === undefined || this.ik_drag_handle === undefined) return
      const target_bone = this.materialized.bones[ik_index]
      IkSolver.solve_ccd(
        target_bone,
        chain_length,
        this.ik_drag_handle.position,
        { iterations: 8, damping: 0.7, pole_vector: pole_vector_world, rotation_limits: limits_map }
      )
      // Solver mutates parent quaternions; sync world matrices of the chain
      // so subsequent renders show the new pose.
      target_bone.updateMatrixWorld(true)
    }
    this.options.transform_controls.addEventListener('change', this.ik_change_listener)

    // On drag end: capture all rotated parent bones' new quaternions and
    // commit them as a single descriptor update. Stored as a field so a
    // mid-drag bone switch can detach it cleanly (otherwise the listener
    // self-removes only on the next drag-end, which may never fire).
    this.ik_drag_end_listener = (e: { value: boolean }) => {
      if (e.value === true) return // drag started, ignore
      this.commit_ik_chain_rotations(ik_index, chain_length)
      // Self-remove on the natural drag-end path so we don't accumulate
      // listeners across multiple drags of the same selection.
      if (this.ik_drag_end_listener !== null) {
        this.options.transform_controls.removeEventListener('dragging-changed', this.ik_drag_end_listener as unknown as () => void)
        this.ik_drag_end_listener = null
      }
    }
    this.options.transform_controls.addEventListener('dragging-changed', this.ik_drag_end_listener as unknown as () => void)
  }

  private detach_ik_drag_handle (): void {
    if (this.ik_change_listener !== null) {
      this.options.transform_controls.removeEventListener('change', this.ik_change_listener)
      this.ik_change_listener = null
    }
    if (this.ik_drag_end_listener !== null) {
      this.options.transform_controls.removeEventListener('dragging-changed', this.ik_drag_end_listener as unknown as () => void)
      this.ik_drag_end_listener = null
    }
    if (this.ik_drag_handle !== undefined) {
      this.options.scene.remove(this.ik_drag_handle)
      this.ik_drag_handle = undefined
    }
  }

  /**
   * Build a Map<Bone, RotationLimitsByAxis> for the IK solver, walking from
   * the IK target's first parent up `chain_length` ancestors and copying
   * each bone's descriptor `rotation_limits` if present.
   */
  private build_chain_limits_map (ik_index: number, chain_length: number): Map<Bone, SwingTwistLimits> | undefined {
    if (this.materialized === undefined) return undefined
    const map = new Map<Bone, SwingTwistLimits>()
    let cursor: Bone | null = this.materialized.bones[ik_index].parent instanceof Object &&
      (this.materialized.bones[ik_index].parent as Bone).isBone === true
      ? this.materialized.bones[ik_index].parent as Bone
      : null
    let walked = 0
    while (cursor !== null && walked < chain_length) {
      const bone_index = this.materialized.bones.indexOf(cursor)
      if (bone_index >= 0) {
        const limits = this.bones[bone_index].rotation_limits
        if (limits !== undefined) map.set(cursor, limits)
      }
      walked++
      cursor = cursor.parent instanceof Object && (cursor.parent as Bone).isBone === true ? cursor.parent as Bone : null
    }
    return map.size > 0 ? map : undefined
  }

  private commit_ik_chain_rotations (ik_index: number, chain_length: number): void {
    if (this.materialized === undefined) return
    const updates: Array<{ index: number, rotation: QuatXYZW }> = []
    let cursor: Bone | null = this.materialized.bones[ik_index].parent instanceof Object &&
      (this.materialized.bones[ik_index].parent as Bone).isBone === true
      ? this.materialized.bones[ik_index].parent as Bone
      : null
    let walked = 0
    while (cursor !== null && walked < chain_length) {
      const bone_index = this.materialized.bones.indexOf(cursor)
      if (bone_index >= 0) {
        updates.push({
          index: bone_index,
          rotation: [cursor.quaternion.x, cursor.quaternion.y, cursor.quaternion.z, cursor.quaternion.w]
        })
      }
      walked++
      cursor = cursor.parent instanceof Object && (cursor.parent as Bone).isBone === true ? cursor.parent as Bone : null
    }
    if (updates.length > 0) {
      const next = BoneTreeEditor.apply_rotations(this.bones, updates)
      this.commit(next, { skip_visual_refresh: true })
    }
  }

  private update_selection_halo (): void {
    // Reset every connector to the default color (each connector owns its
    // material instance, so this is a cheap field write per bone).
    for (const connector of this.bone_connectors) {
      if (connector === undefined) continue
      const mat = connector.material
      if (mat instanceof MeshBasicMaterial) mat.color.setHex(this.default_connector_color)
    }

    if (this.selection_halo === undefined) return
    if (this.selection_halo.parent !== null) {
      this.selection_halo.parent.remove(this.selection_halo)
    }
    if (this.selected_bone_index === null || this.materialized === undefined) {
      this.selection_halo.visible = false
      return
    }
    const bone = this.materialized.bones[this.selected_bone_index]
    bone.add(this.selection_halo)
    this.selection_halo.position.set(0, 0, 0)
    this.selection_halo.visible = true

    const selected_connector = this.bone_connectors[this.selected_bone_index]
    if (selected_connector !== undefined) {
      const mat = selected_connector.material
      if (mat instanceof MeshBasicMaterial) mat.color.setHex(this.selected_connector_color)
    }
  }

  private clear_visuals (): void {
    while (this.visual_root.children.length > 0) {
      this.visual_root.remove(this.visual_root.children[0])
    }
    for (const line of this.pole_indicators.values()) {
      line.geometry.dispose()
      const m = line.material
      if (Array.isArray(m)) m.forEach(mm => mm.dispose())
      else m.dispose()
    }
    this.pole_indicators.clear()
    if (this.selection_halo !== undefined) {
      this.selection_halo.geometry.dispose()
      const mat = this.selection_halo.material
      if (Array.isArray(mat)) mat.forEach(m => m.dispose())
      else mat.dispose()
      this.selection_halo = undefined
    }
    // Each connector owns its own material; geometry is shared so dispose
    // only the first one's geometry once.
    let connector_geo_disposed = false
    for (const connector of this.bone_connectors) {
      if (connector === undefined) continue
      const mat = connector.material
      if (Array.isArray(mat)) mat.forEach(m => m.dispose())
      else mat.dispose()
      if (!connector_geo_disposed) {
        connector.geometry.dispose()
        connector_geo_disposed = true
      }
    }
    this.bone_connectors.length = 0
    // Head handles each own a material (per-bone for lock/IK tinting). Their
    // geometries come from BoneShapeGeometry's lifetime-of-the-page cache
    // and must NOT be disposed here (still shared). Tail handles share one
    // material and one geometry — dispose each exactly once.
    let tail_geo_disposed = false
    let tail_mat_disposed = false
    const head_count = this.bone_handles.length / 2
    for (let i = 0; i < this.bone_handles.length; i++) {
      const handle = this.bone_handles[i]
      const mat = handle.material
      if (i < head_count) {
        if (Array.isArray(mat)) mat.forEach(m => m.dispose())
        else mat.dispose()
      } else {
        if (!tail_mat_disposed) {
          if (Array.isArray(mat)) mat.forEach(m => m.dispose())
          else mat.dispose()
          tail_mat_disposed = true
        }
        if (!tail_geo_disposed) { handle.geometry.dispose(); tail_geo_disposed = true }
      }
    }
    this.bone_handles.length = 0
    // AxesHelpers are owned by the materialized bones (parented to them); the
    // bones themselves are dropped along with `materialized` here, so there's
    // nothing further to remove. Just forget our references so the next
    // `refresh_visuals` rebuilds cleanly.
    this.bone_axis_helpers.length = 0
  }

  private notify_render_listeners (): void {
    for (const listener of this.render_listeners) {
      try {
        listener()
      } catch (err) {
        console.warn('StepBuildSkeleton render listener threw:', err)
      }
    }
  }
}

function escape_regex (literal: string): string {
  return literal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Generate a distinct color per bone index for the influence preview. Uses an
 * evenly-spaced HSL hue with constant saturation/lightness; the golden-ratio
 * stride spreads sequential indices apart on the color wheel so neighbours
 * stay distinguishable without precomputing a fixed palette.
 */
function generate_palette_color (index: number): Color {
  const hue = (index * 0.61803398875) % 1
  return new Color().setHSL(hue, 0.65, 0.55)
}

function clone_bone (b: BoneDescriptor): BoneDescriptor {
  return {
    name: b.name,
    parent_index: b.parent_index,
    head: [b.head[0], b.head[1], b.head[2]],
    tail: [b.tail[0], b.tail[1], b.tail[2]],
    rotation_quaternion: [b.rotation_quaternion[0], b.rotation_quaternion[1], b.rotation_quaternion[2], b.rotation_quaternion[3]]
  }
}
